/**
 * G8 research library: what the agent (or the user) researched during conversations, sealed per
 * project or per user. Every source opens; every entry links back to the conversation and run
 * it came from. Mounted in the project (Research tab) and in You → Research.
 */
import { Link } from '@tanstack/react-router'
import { Chip } from '@viko/transcript-ui'
import { BookOpen, ExternalLink, MessageSquare, Search } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import { useProjects } from '../../lib/queries.js'
import { filterResearch, type ResearchEntry, sourceHost, useResearch } from '../../lib/research.js'
import { Ago, Empty, Input, SkeletonRows } from '../ui.js'

export function ResearchEntryCard({ r, projectName }: { r: ResearchEntry; projectName?: string }) {
  const headingId = useId()
  return (
    <article
      aria-labelledby={headingId}
      className="grid gap-2 rounded-xl border border-border-subtle bg-surface-1 p-3"
      data-testid="research-entry"
    >
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h3 id={headingId} className="m-0 text-[15px] font-semibold text-text-1">
          {r.title}
        </h3>
        <span className="text-[14px] text-text-3">
          <Ago ts={r.createdAt} /> ·{' '}
          <time dateTime={new Date(r.createdAt).toISOString()}>
            {new Date(r.createdAt).toLocaleDateString()}
          </time>
        </span>
        <span className="flex-1" />
        <Chip>{r.scope === 'user' ? 'Yours' : (projectName ?? 'Project')}</Chip>
        {r.by === 'agent' ? <Chip>Agent research</Chip> : null}
      </header>
      {r.query && r.query !== r.title ? (
        <p className="m-0 text-[14px] text-text-2">Question: {r.query}</p>
      ) : null}
      <p className="m-0 whitespace-pre-wrap text-[14px] text-text-1">{r.summary}</p>
      <section aria-label={`Sources (${r.sources.length})`}>
        <h4 className="m-0 text-[14px] font-semibold text-text-2">Sources · {r.sources.length}</h4>
        <ul className="m-0 grid list-none gap-1 p-0">
          {r.sources.map((s) => (
            <li key={s.url} className="text-[14px]">
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-baseline gap-1 text-accent-text"
              >
                {s.title}
                <span className="text-[14px] text-text-3">{sourceHost(s.url)}</span>
                <ExternalLink size={12} aria-hidden="true" />
              </a>
              {s.note ? <span className="block text-[14px] text-text-2">{s.note}</span> : null}
            </li>
          ))}
        </ul>
      </section>
      {r.conversation?.barkId || r.conversation?.runId || r.tags.length ? (
        <footer className="flex flex-wrap items-center gap-2 text-[14px]">
          {r.conversation?.barkId && r.projectId ? (
            <Link
              to="/p/$projectId"
              params={{ projectId: r.projectId }}
              search={{ i: `bark:${r.conversation.barkId}` }}
              className="inline-flex items-center gap-1 text-accent-text"
            >
              <MessageSquare size={12} aria-hidden="true" /> Open the conversation
            </Link>
          ) : null}
          {r.conversation?.runId ? (
            <Link
              to="/s/$runId"
              params={{ runId: r.conversation.runId }}
              className="text-accent-text"
            >
              Open the agent session
            </Link>
          ) : null}
          {r.tags.map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </footer>
      ) : null}
    </article>
  )
}

export function ResearchLibrary({ projectId, scope }: { projectId?: string; scope?: 'user' }) {
  const b = useBackend()
  const q = useResearch()
  const projects = useProjects()
  const [query, setQuery] = useState('')
  const searchId = useId()
  const names = useMemo(
    () => new Map((projects.data ?? []).map((p) => [p.id, p.name])),
    [projects.data],
  )
  const rows = useMemo(
    () =>
      filterResearch(q.data ?? [], {
        ...(projectId ? { projectId } : {}),
        ...(scope ? { scope } : {}),
        query,
      }),
    [q.data, projectId, scope, query],
  )
  const total = useMemo(
    () =>
      filterResearch(q.data ?? [], {
        ...(projectId ? { projectId } : {}),
        ...(scope ? { scope } : {}),
      }).length,
    [q.data, projectId, scope],
  )
  if (!b.entities)
    return (
      <Empty title="Research is end-to-end encrypted" icon={<BookOpen size={20} />}>
        Unlock this browser to read the research library.
      </Empty>
    )
  if (q.isPending) return <SkeletonRows />
  if (q.isError)
    return <Empty title="Could not load research">Check the connection and try again.</Empty>
  return (
    <section className="grid gap-3 p-4" aria-label="Research library">
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor={searchId}
          className="inline-flex items-center gap-1 text-[14px] text-text-2"
        >
          <Search size={14} aria-hidden="true" /> Search
        </label>
        <Input
          id={searchId}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Titles, findings, sources"
          className="min-w-0 flex-1"
        />
        <span className="text-[14px] text-text-3" aria-live="polite">
          {query ? `${rows.length} of ${total}` : `${total} ${total === 1 ? 'entry' : 'entries'}`}
        </span>
      </div>
      {rows.length ? (
        rows.map((r) => (
          <ResearchEntryCard
            key={r.id}
            r={r}
            {...(r.projectId && names.get(r.projectId)
              ? { projectName: names.get(r.projectId) }
              : {})}
          />
        ))
      ) : (
        <Empty
          title={query ? 'No matching research' : 'No research yet'}
          icon={<BookOpen size={20} />}
        >
          {query
            ? 'Try another word, or clear the search.'
            : 'Discuss an idea with the project agent (by voice or text). What it researches — with every source — is saved here, encrypted.'}
        </Empty>
      )}
    </section>
  )
}
